import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { Loader2, Target, TrendingUp, Crosshair, AlertCircle } from "lucide-react";

/**
 * Target Sight — system-derived ceiling-only readout on the Ledger.
 *
 * No floor, no user-set target. Three ceilings, two paces, one
 * projection. Numbers come from /api/sight/target which counts
 * life_entries per day and aggregates pace + percentile.
 */
export default function TargetSight() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/sight/target");
                if (!cancelled) setData(data);
            } catch (e) {
                if (!cancelled) setErr(e?.response?.data?.detail || e.message || "Sight failed");
            } finally {
                if (!cancelled) setBusy(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    return (
        <Shell>
            <div className="mb-8">
                <div className="label-typed tw-prefix">Volume vs Pace · System-derived · No floor</div>
                <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1 flex items-center gap-3">
                    Target Sight<span className="cursor-blink" />
                </h1>
                <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                    Three ceilings. Two paces. One projection. The Shed observes how fast
                    you file, how fast you've ever filed, and how far that puts you
                    inside the next round number. No target you can game.
                </p>
            </div>

            {busy && (
                <div className="paper-card p-12 text-center" data-testid="sight-busy">
                    <Loader2 className="w-7 h-7 text-stamp-red mx-auto mb-3 animate-spin" />
                    <div className="label-typed text-stamp-red">Sighting in…</div>
                </div>
            )}

            {err && !busy && (
                <div className="paper-card border-2 border-stamp-red p-5" data-testid="sight-error">
                    <div className="flex items-center gap-2 stamp-red font-heading text-sm tracking-[0.2em] mb-2">
                        <AlertCircle className="w-4 h-4" /> SIGHT FAILED
                    </div>
                    <pre className="font-body text-xs text-ink whitespace-pre-wrap">{err}</pre>
                </div>
            )}

            {data && !busy && (
                <div className="space-y-8" data-testid="sight-content">
                    {/* Live pace · over/under indicators (iter 31.20) */}
                    <section data-testid="sight-indicators">
                        <div className="label-typed mb-3 flex items-center gap-2">
                            <TrendingUp className="w-3 h-3 text-stamp-red" />
                            7-day pace · {Number(data.pace_7d || 0).toFixed(2)} entries / day
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <DeltaCard
                                label="vs 30-day pace"
                                pct={data.indicators?.vs_30d_pct}
                                baseline={data.pace_30d}
                                baselineUnit="/ day"
                                mode="delta"
                                testid="sight-vs-30d"
                            />
                            <DeltaCard
                                label="vs lifetime pace"
                                pct={data.indicators?.vs_lifetime_pct}
                                baseline={data.avg_lifetime_pace}
                                baselineUnit="/ day"
                                mode="delta"
                                testid="sight-vs-lifetime"
                            />
                            <DeltaCard
                                label="% of peak 7-day"
                                pct={data.indicators?.pct_of_peak_7d}
                                baseline={data.ceiling_peak_7d_rolling}
                                baselineUnit="/ day · ceiling"
                                mode="of"
                                testid="sight-of-peak"
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <PaceCard
                                label="7-day pace"
                                value={data.pace_7d}
                                sub="entries / day · rolling 7"
                                testid="sight-pace-7d"
                            />
                            <PaceCard
                                label="30-day pace"
                                value={data.pace_30d}
                                sub="entries / day · rolling 30"
                                testid="sight-pace-30d"
                            />
                        </div>
                    </section>

                    {/* Three ceilings */}
                    <section>
                        <div className="label-typed mb-3 flex items-center gap-2">
                            <Crosshair className="w-3 h-3 text-stamp-red" />
                            Ceilings · observed, not set
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <CeilingCard
                                label="Peak day"
                                value={data.ceiling_peak_day}
                                unit="entries"
                                tone="Single highest-volume day on the chain"
                                testid="sight-ceiling-peak-day"
                            />
                            <CeilingCard
                                label="Peak 7-day"
                                value={data.ceiling_peak_7d_rolling}
                                unit="entries / day"
                                tone="Best sustained 7-day rolling mean"
                                testid="sight-ceiling-peak-7d"
                            />
                            <CeilingCard
                                label="P95 daily"
                                value={data.ceiling_p95_daily}
                                unit="entries / day"
                                tone="95th-percentile day — outliers excluded"
                                testid="sight-ceiling-p95"
                            />
                        </div>
                    </section>

                    {/* Projection */}
                    <section className="paper-card border-2 border-ink/30 p-6 relative" data-testid="sight-projection">
                        <div className="absolute top-3 right-4 classified-stamp">FORWARD</div>
                        <div className="flex items-center gap-2 mb-4">
                            <TrendingUp className="w-4 h-4 text-stamp-red" />
                            <div className="font-heading uppercase tracking-[0.2em] text-sm text-ink">
                                At current 7-day pace
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <div className="label-typed">In 30 days</div>
                                <div className="font-heading text-3xl text-ink mt-1" data-testid="sight-proj-30d">
                                    +{data.projection_30d_entries}
                                </div>
                                <div className="font-body text-xs text-ink-soft">new filings</div>
                            </div>
                            <div>
                                <div className="label-typed">In 90 days</div>
                                <div className="font-heading text-3xl text-ink mt-1" data-testid="sight-proj-90d">
                                    +{data.projection_90d_entries}
                                </div>
                                <div className="font-body text-xs text-ink-soft">new filings</div>
                            </div>
                            <div>
                                <div className="label-typed">Next milestone</div>
                                {data.next_milestone ? (
                                    <>
                                        <div className="font-heading text-3xl text-stamp-red mt-1" data-testid="sight-next-milestone">
                                            {data.next_milestone.target.toLocaleString()}
                                        </div>
                                        <div className="font-body text-xs text-ink-soft">
                                            ETA {data.next_milestone.eta} ·{" "}
                                            {data.next_milestone.days_out} days out
                                        </div>
                                    </>
                                ) : (
                                    <div className="font-body text-sm text-ink-soft mt-1" data-testid="sight-next-milestone-empty">
                                        Waiting on 7-day pace &gt; 0.
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>

                    {/* Chain footer */}
                    <section className="paper-card p-5" data-testid="sight-chain">
                        <div className="flex flex-wrap items-baseline gap-6">
                            <ChainStat label="Chain total" value={data.chain_total.toLocaleString()} />
                            <ChainStat label="Lifetime" value={`${data.lifetime_days} d`} />
                            <ChainStat label="Lifetime pace" value={`${data.avg_lifetime_pace} / d`} />
                            <ChainStat label="First entry" value={(data.first_entry || "").slice(0, 10) || "—"} />
                            <ChainStat label="Last entry" value={(data.last_entry || "").slice(0, 10) || "—"} />
                        </div>
                    </section>

                    <div className="text-center pencil text-base text-ink-soft mt-2">
                        — observed, not set —
                    </div>
                </div>
            )}
        </Shell>
    );
}

function PaceCard({ label, value, sub, testid }) {
    return (
        <div className="paper-card p-6 relative tilt-l" data-testid={testid}>
            <div className="label-typed flex items-center gap-2">
                <Target className="w-3 h-3 text-stamp-red" /> {label}
            </div>
            <div className="font-heading text-6xl text-ink mt-2 tracking-tight">
                {Number(value || 0).toFixed(2)}
            </div>
            <div className="font-body text-xs text-ink-soft uppercase tracking-[0.18em] mt-1">{sub}</div>
        </div>
    );
}

function CeilingCard({ label, value, unit, tone, testid }) {
    return (
        <div className="paper-card border border-ink/30 p-5" data-testid={testid}>
            <div className="label-typed text-stamp-red">{label}</div>
            <div className="font-heading text-4xl text-ink mt-1">
                {typeof value === "number" ? value.toLocaleString() : value}
            </div>
            <div className="font-body text-[10px] text-ink-soft uppercase tracking-[0.18em]">{unit}</div>
            <div className="font-body text-xs text-ink mt-3 leading-snug">{tone}</div>
        </div>
    );
}

function ChainStat({ label, value }) {
    return (
        <div>
            <div className="label-typed text-[10px]">{label}</div>
            <div className="font-heading text-lg text-ink">{value}</div>
        </div>
    );
}

// iter 31.20 — over/under indicator. `mode="delta"` shows signed % vs a
// baseline (green over, amber on-pace, red under); `mode="of"` shows
// % of an observed ceiling (green near-peak, amber mid, red far below).
function DeltaCard({ label, pct, baseline, baselineUnit, mode, testid }) {
    const numeric = typeof pct === "number" && Number.isFinite(pct);
    let band = "amber";
    let copy = "—";
    let sign = "";
    if (!numeric) {
        band = "muted";
        copy = "Not enough data";
    } else if (mode === "delta") {
        if (pct > 10) band = "green";
        else if (pct < -10) band = "red";
        else band = "amber";
        sign = pct > 0 ? "+" : "";
        copy = `${sign}${pct.toFixed(1)}%`;
    } else {
        // mode === "of"
        if (pct >= 80) band = "green";
        else if (pct >= 50) band = "amber";
        else band = "red";
        copy = `${pct.toFixed(1)}%`;
    }
    const tone = {
        green: { ring: "border-stamp-green",  ink: "text-stamp-green",  label: "OVER" },
        amber: { ring: "border-stamp-red/40", ink: "text-ink",          label: "ON PACE" },
        red:   { ring: "border-stamp-red",    ink: "text-stamp-red",    label: "UNDER" },
        muted: { ring: "border-ink/15",       ink: "text-ink-soft",     label: "—" },
    }[band];
    // override stamp label for "of" mode so it reads more like ceiling proximity
    let stampLabel = tone.label;
    if (mode === "of" && numeric) {
        if (band === "green") stampLabel = "NEAR PEAK";
        else if (band === "amber") stampLabel = "MID-RANGE";
        else stampLabel = "FAR FROM PEAK";
    }
    return (
        <div className={`paper-card border-2 p-5 relative ${tone.ring}`} data-testid={testid}>
            <div className="absolute top-2 right-3 classified-stamp text-[9px]">{stampLabel}</div>
            <div className="label-typed">{label}</div>
            <div className={`font-heading text-5xl mt-2 tracking-tight ${tone.ink}`}>{copy}</div>
            <div className="font-body text-[10px] text-ink-soft mt-2 uppercase tracking-[0.15em]">
                baseline {Number(baseline || 0).toFixed(2)} {baselineUnit}
            </div>
        </div>
    );
}
